import React, { useState, useEffect } from 'react'
import { X, Building2, Mail, Phone, Globe, FileText, User, PoundSterling } from 'lucide-react'
import { agencyService } from '../services/agencyService'
import { settingsService } from '../services/settingsService'
import { userService } from '../services/userService'

interface AddAgencyModalProps {
  isOpen: boolean
  onClose: () => void
  onAgencyAdded: (agencyId?: string) => void
}

interface AgencyFormData {
  name: string
  agencyCode: string
  phone: string
  website: string
  description: string
  agencyAdminEmail: string
  agencyBasePlanCost: number
  agencySeatCost: number
}

interface ValidationErrors {
  phone?: string
  website?: string
  agencyAdminEmail?: string
}

export const AddAgencyModal: React.FC<AddAgencyModalProps> = ({
  isOpen,
  onClose,
  onAgencyAdded
}) => {
  const [formData, setFormData] = useState<AgencyFormData>({
    name: '',
    agencyCode: '',
    phone: '',
    website: '',
    description: '',
    agencyAdminEmail: '',
    agencyBasePlanCost: 0,
    agencySeatCost: 0
  })

  const [isLoading, setIsLoading] = useState(false)
  const [isCheckingCode, setIsCheckingCode] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [codeAvailable, setCodeAvailable] = useState<boolean | null>(null)
  const [settings, setSettings] = useState<any>(null)
  const [touchedFields, setTouchedFields] = useState<Set<keyof AgencyFormData>>(new Set())
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({})

  // Load default settings when modal opens
  useEffect(() => {
    if (isOpen && !settings) {
      loadDefaultSettings()
    }
  }, [isOpen])

  const loadDefaultSettings = async () => {
    try {
      const defaultSettings = await settingsService.getSettings()
      setSettings(defaultSettings)
      setFormData(prev => ({
        ...prev,
        agencyBasePlanCost: defaultSettings.defaultBasePlanCost,
        agencySeatCost: defaultSettings.defaultAgencySeatCost
      }))
    } catch (error) {
      console.error('Error loading settings:', error)
      // Set default values if settings can't be loaded
      setFormData(prev => ({
        ...prev,
        agencyBasePlanCost: 2900, // £29.00 in pence
        agencySeatCost: 1000 // £10.00 in pence
      }))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validate required fields
    const errors: ValidationErrors = {}

    if (!formData.name.trim()) {
      setError('Agency name is required')
      return
    }

    if (!formData.agencyCode.trim()) {
      setError('Agency code is required')
      return
    }

    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required'
    }

    if (!formData.website.trim()) {
      errors.website = 'Website is required'
    } else if (!/^https?:\/\/.+/.test(formData.website.trim())) {
      errors.website = 'Website should start with http:// or https://'
    }

    if (!formData.agencyAdminEmail.trim()) {
      setError('Agency admin email is required')
      return
    }

    // Check if there are validation errors
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      // Create agency with admin user
      const createdAgency = await agencyService.createAgencyWithAdmin({
        name: formData.name,
        agencyCode: formData.agencyCode,
        phone: formData.phone || undefined,
        website: formData.website || undefined,
        description: formData.description || undefined,
        agencyAdminEmail: formData.agencyAdminEmail,
        agencyBasePlanCost: formData.agencyBasePlanCost,
        agencySeatCost: formData.agencySeatCost
      })

      onAgencyAdded(createdAgency.id)
      onClose()

      // Reset form
      setFormData({
        name: '',
        agencyCode: '',
        phone: '',
        website: '',
        description: '',
        agencyAdminEmail: '',
        agencyBasePlanCost: settings?.defaultBasePlanCost || 2900,
        agencySeatCost: settings?.defaultAgencySeatCost || 1000
      })
      setCodeAvailable(null)
      setTouchedFields(new Set())
      setValidationErrors({})
    } catch (err: any) {
      setError(err.message || 'Failed to create agency')
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (field: keyof AgencyFormData, value: string | number) => {
    // Mark field as touched
    setTouchedFields(prev => new Set(prev).add(field))

    setFormData(prev => ({
      ...prev,
      [field]: value
    }))

    // Auto-generate agency code from name (same logic as wizard)
    if (field === 'name' && typeof value === 'string') {
      const generatedCode = value
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '')
        .substring(0, 50)

      setFormData(prev => ({
        ...prev,
        [field]: value,
        agencyCode: generatedCode
      }))

      // Check availability of generated code
      if (generatedCode) {
        setTimeout(() => checkAgencyCode(generatedCode), 500)
      }
    }

    // Clear validation errors when user starts typing
    if (validationErrors[field as keyof ValidationErrors]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: undefined
      }))
    }
  }

  const checkAgencyCode = async (code: string) => {
    if (!code || code.length < 2) {
      setCodeAvailable(null)
      return
    }

    if (!/^[a-z0-9-]+$/.test(code)) {
      setCodeAvailable(false)
      return
    }

    setIsCheckingCode(true)
    try {
      const result = await agencyService.checkAgencyCodeAvailability(code)
      setCodeAvailable(result.available)
    } catch (error: any) {
      console.error('Error checking agency code:', error)
      setCodeAvailable(false)
    } finally {
      setIsCheckingCode(false)
    }
  }

  const handleAgencyCodeChange = (value: string) => {
    // Clean the code to match the wizard logic
    const cleanedCode = value
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '')

    handleInputChange('agencyCode', cleanedCode)

    // Debounce the code check
    setTimeout(() => checkAgencyCode(cleanedCode), 500)
  }

  const formatCurrency = (pence: number) => {
    return `£${(pence / 100).toFixed(2)}`
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-slate-800 rounded-lg max-w-2xl w-full mx-4 max-h-screen overflow-y-auto border border-slate-200 dark:border-slate-700">
        <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 dark:bg-primary-900/20 rounded-lg">
              <Building2 className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Create Agency</h2>
              <p className="text-sm text-slate-600 dark:text-slate-400">Add a new agency to the system</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-500 dark:text-slate-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 rounded-lg">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Agency Name */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                <Building2 className="w-4 h-4 inline mr-2" />
                Agency Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="w-full p-3 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Enter agency name"
                required
              />
            </div>

            {/* Agency Code */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Agency Code *
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={formData.agencyCode}
                  onChange={(e) => handleAgencyCodeChange(e.target.value)}
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${codeAvailable === true
                    ? 'border-green-300 dark:border-green-600 bg-white dark:bg-slate-700'
                    : codeAvailable === false
                      ? 'border-red-300 dark:border-red-600 bg-white dark:bg-slate-700'
                      : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700'
                    } text-slate-900 dark:text-white`}
                  placeholder="Enter agency code"
                  required
                />
                {isCheckingCode && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="w-4 h-4 border-2 border-slate-300 border-t-primary-600 rounded-full animate-spin" />
                  </div>
                )}
                {codeAvailable === true && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-green-600">
                    ✓
                  </div>
                )}
                {codeAvailable === false && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-red-600">
                    ✗
                  </div>
                )}
              </div>
              {codeAvailable === false && (
                <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                  This agency code is already taken
                </p>
              )}
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Agency code will be auto-generated from the agency name, or you can enter it manually
              </p>
            </div>

            {/* Phone Number */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                <Phone className="w-4 h-4 inline mr-2" />
                Phone Number <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                onBlur={() => setTouchedFields(prev => new Set(prev).add('phone'))}
                className="w-full p-3 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="+44 123 456 7890"
                required
              />
              {touchedFields.has('phone') && formData.phone.length === 0 && (
                <p className="text-red-500 text-sm mt-1">Phone number is required</p>
              )}
              {validationErrors.phone && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.phone}</p>
              )}
            </div>

            {/* Website */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                <Globe className="w-4 h-4 inline mr-2" />
                Website <span className="text-red-500">*</span>
              </label>
              <input
                type="url"
                value={formData.website}
                onChange={(e) => handleInputChange('website', e.target.value)}
                onBlur={() => setTouchedFields(prev => new Set(prev).add('website'))}
                className="w-full p-3 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="https://example.com"
                required
              />
              {touchedFields.has('website') && formData.website.length === 0 && (
                <p className="text-red-500 text-sm mt-1">Website is required</p>
              )}
              {formData.website.length > 0 && !/^https?:\/\/.+/.test(formData.website) && (
                <p className="text-amber-600 text-sm mt-1">Website should start with http:// or https://</p>
              )}
              {validationErrors.website && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.website}</p>
              )}
            </div>

            {/* Description */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                <FileText className="w-4 h-4 inline mr-2" />
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={3}
                className="w-full p-3 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Enter agency description"
              />
            </div>

            {/* Agency Admin Email */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                <User className="w-4 h-4 inline mr-2" />
                Agency Admin Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={formData.agencyAdminEmail}
                onChange={(e) => handleInputChange('agencyAdminEmail', e.target.value)}
                onBlur={() => setTouchedFields(prev => new Set(prev).add('agencyAdminEmail'))}
                className="w-full p-3 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="admin@agency.com"
                required
              />
              {touchedFields.has('agencyAdminEmail') && formData.agencyAdminEmail.length === 0 && (
                <p className="text-red-500 text-sm mt-1">Agency admin email is required</p>
              )}
              {validationErrors.agencyAdminEmail && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.agencyAdminEmail}</p>
              )}
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                If the user exists, they will be assigned as admin. If not, a new user will be created.
              </p>
            </div>

            {/* Agency Base Plan Cost */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                <PoundSterling className="w-4 h-4 inline mr-2" />
                Agency Base Plan Cost (pence)
              </label>
              <input
                type="number"
                value={formData.agencyBasePlanCost}
                onChange={(e) => handleInputChange('agencyBasePlanCost', parseInt(e.target.value) || 0)}
                className="w-full p-3 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="2900"
                min="0"
              />
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                {formatCurrency(formData.agencyBasePlanCost)} per month
              </p>
            </div>

            {/* Agency Seat Cost */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                <PoundSterling className="w-4 h-4 inline mr-2" />
                Agency Seat Cost (pence)
              </label>
              <input
                type="number"
                value={formData.agencySeatCost}
                onChange={(e) => handleInputChange('agencySeatCost', parseInt(e.target.value) || 0)}
                className="w-full p-3 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="1000"
                min="0"
              />
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                {formatCurrency(formData.agencySeatCost)} per seat per month
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg transition-colors"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !formData.name.trim() || !formData.agencyCode.trim() || !formData.phone.trim() || !formData.website.trim() || !formData.agencyAdminEmail.trim() || codeAvailable === false || Object.keys(validationErrors).length > 0}
              className="flex-1 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-slate-400 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Building2 className="w-4 h-4" />
                  Create Agency
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AddAgencyModal 